import { motion } from "motion/react"
import { useEffect, useRef, useState } from "react"

interface CounterProps {
  value: number
  className?: string
  style?: React.CSSProperties
}

const getStepSize = (delta: number) => {
  const absDelta = Math.abs(delta)
  if (absDelta <= 10) return 1
  if (absDelta <= 50) return 2
  if (absDelta <= 100) return 5
  if (absDelta <= 500) return 10
  if (absDelta <= 1000) return 25
  return Math.ceil(absDelta / 40)
}

const Counter = ({ value = 0, className, style }: CounterProps) => {
  const [animatedValue, setAnimatedValue] = useState(value)
  const [isAnimating, setIsAnimating] = useState(false)
  const prevValueRef = useRef(value)

  useEffect(() => {
    const from = prevValueRef.current
    prevValueRef.current = value

    if (from === value) return

    if (value === 0) {
      setAnimatedValue(0)
      setIsAnimating(false)
      return
    }

    const delta = value - from
    const absDelta = Math.abs(delta)
    const stepSize = getStepSize(delta)
    const MAX_DURATION = 750
    const totalDuration =
      absDelta <= 1 ? 0 : Math.min((absDelta / 10) * MAX_DURATION, MAX_DURATION)
    const startTime = Date.now()
    let animationId: number

    if (totalDuration === 0) {
      setAnimatedValue(value)
      setIsAnimating(false)
      return
    }

    setIsAnimating(true)

    const animate = () => {
      const elapsed = Date.now() - startTime
      const progress = Math.min(elapsed / totalDuration, 1)

      if (progress === 1) {
        setAnimatedValue(value)
        setIsAnimating(false)
        return
      }

      const easedProgress = 1 - Math.pow(1 - progress, 3)
      const rawValue = from + easedProgress * delta
      const stepped =
        delta > 0
          ? Math.min(Math.floor(rawValue / stepSize) * stepSize, value)
          : Math.max(Math.ceil(rawValue / stepSize) * stepSize, value)

      setAnimatedValue(stepped)
      animationId = requestAnimationFrame(animate)
    }

    setAnimatedValue(from)
    animationId = requestAnimationFrame(animate)

    return () => {
      if (animationId) {
        cancelAnimationFrame(animationId)
      }
    }
  }, [value])

  return (
    <span
      className={className}
      style={{
        margin: 0,
        padding: 0,
        ...style,
      }}
    >
      <motion.div
        animate={{
          scale: isAnimating ? [1, 1.05, 1] : 1,
        }}
        transition={{
          duration: 0.1,
          ease: "easeOut",
        }}
        style={{
          display: "inline-block",
        }}
      >
        {animatedValue}
      </motion.div>
    </span>
  )
}

export default Counter
